import styled from '@emotion/styled';
import { useMemo } from 'react';
import type { NpsValue } from '@hotjar/webapp/features/Surveys/Results/NPSTable/NPSTable';
import { NPSTable } from '@hotjar/webapp/features/Surveys/Results/NPSTable/NPSTable';
import type { NpsResult } from '@hotjar/feedback/domain/Surveys/Results/AggregatedResult';
import { Stack, tokens } from '@hotjar/hotsauce';
import type { DateRange } from '@hotjar/global-filters';
import { useTranslation } from '@hotjar/translations';
import { useSurveyViewStore } from '@hotjar/feedback/domain/Surveys/surveyViewStore';
import { NPSLineChartWithResolution } from '@hotjar/webapp/features/Surveys/Results/NPSLineChartWithResolution/NPSLineChartWithResolution';
import { NPSStackedBarChart } from '@hotjar/webapp/features/Surveys/Results/NPSStackedBarChart';
import { NPSGradientChart } from '../NPSGradientChart';
import { NPSTag } from './NPSTag';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 7fr 1fr 4fr;

  @media (max-width: 1160px) {
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacer.x4};
  }
`;

const GraphColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x6};
`;

const TableColumn = styled.div`
  grid-column: 3;
  display: flex;
  flex-direction: column;
`;

const NPSMessage = styled.span`
  ${tokens.text('caption')};
  max-width: 512px;
  margin: ${tokens.spacer.x4} auto;
  color: ${tokens.color.text_disabled};
`;

interface ScorePerResolution {
  scorePerMonth: Record<string, number>;
  scorePerWeek: Record<string, number>;
  scorePerDay: Record<string, number>;
}

const possibleScores = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export const NPSView = ({ result }: { result: NpsResult }): JSX.Element | null => {
  const { t, i18n } = useTranslation('feedback', { keyPrefix: 'results' });
  const { globalFilters } = useSurveyViewStore();
  const dateFilter = globalFilters.dateRange;

  const [scoresPerResolution, dateRangeSettings, npsValues]:
    | [ScorePerResolution, DateRange, NpsValue[]]
    | [null, null, null] = useMemo(() => {
    if (!('score_per_day' in result)) return [null, null, null];

    if (dateFilter === undefined) return [null, null, null];

    const scoresResolution = {
      scorePerDay: result.score_per_day,
      scorePerWeek: result.score_per_week,
      scorePerMonth: result.score_per_month,
    };

    const totalResponsesInDayRange = result.total;

    const countsByResponse = result.answers_totals;

    const resultsTable: NpsValue[] = possibleScores.map((score) => {
      const value = countsByResponse[score] || 0;
      return {
        label: score,
        value,
        percentage: Number(value / totalResponsesInDayRange).toLocaleString(i18n.language, {
          style: 'percent',
        }),
      };
    });

    return [scoresResolution, dateFilter, resultsTable];
  }, [dateFilter, result, i18n.language]);

  return (
    <Container>
      <NPSLineChartWithResolution
        scoresPerResolution={scoresPerResolution}
        dateRangeSettings={dateRangeSettings}
      />

      <Columns>
        <GraphColumn>
          <Stack direction="column" spacing="x1">
            <span>
              {t('nps-view-mode.overall')}
              <NPSTag append=":" />
            </span>

            <NPSGradientChart score={result.score} />
          </Stack>
          <Stack direction="column" spacing="x1">
            {t('nps-view-mode.overall-distribution')}
            <NPSStackedBarChart
              detractors={result.detractors}
              passives={result.passives}
              promoters={result.promoters}
            />
          </Stack>
        </GraphColumn>
        <TableColumn>
          <NPSTable npsValues={npsValues} />
        </TableColumn>
      </Columns>

      <NPSMessage>{t('nps-view-mode.trademark-message')}</NPSMessage>
    </Container>
  );
};
